import {useEffect, useState} from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {formatCurrency} from '../data';
import {
  getPaymentSettings,
  getShipping,
  getStorePickupSettings,
  savePaymentSetting,
  saveShippingDistrict,
  saveStorePickupSettings
} from '../services/adminManagementService';
import {removeDeliveryDistrict} from '../services/deliveryDistrictService';
import './checkout-delivery-adjustments.css';

const {FiCheckCircle, FiPlus, FiSave, FiMapPin, FiInfo, FiTrash2} = FiIcons;

const bankFieldTemplate = [
  {key: 'bank_name', label: 'Bank Name', placeholder: 'Bank name'},
  {key: 'branch_name', label: 'Branch Name', placeholder: 'Branch name'},
  {key: 'account_name', label: 'Account Name', placeholder: 'Account holder'},
  {key: 'account_number', label: 'Account Number', placeholder: 'Account number'},
  {key: 'bank_code', label: 'Bank Code (optional)', placeholder: ''},
  {key: 'branch_code', label: 'Branch Code (optional)', placeholder: ''}
];

function parseBankInstructions(instructions) {
  const fields = {};
  const patterns = {
    bank_name: /^Bank Name:\s*(.*)$/i,
    branch_name: /^Branch Name:\s*(.*)$/i,
    account_name: /^Account Name:\s*(.*)$/i,
    account_number: /^Account Number:\s*(.*)$/i,
    bank_code: /^Bank Code:\s*(.*)$/i,
    branch_code: /^Branch Code:\s*(.*)$/i
  };
  const extra = [];
  for (const line of String(instructions || '').split('\n')) {
    let matched = false;
    for (const [key, pattern] of Object.entries(patterns)) {
      const result = line.match(pattern);
      if (result) {
        fields[key] = result[1].trim();
        matched = true;
        break;
      }
    }
    if (!matched) extra.push(line);
  }
  return {fields, extra: extra.join('\n').trim()};
}

function buildBankInstructions(fields, extra) {
  const lines = bankFieldTemplate
    .filter(({key}) => String(fields[key] || '').trim())
    .map(({key, label}) => `${label.replace(' (optional)', '')}: ${fields[key].trim()}`);
  if (extra.trim()) lines.push(extra.trim());
  return lines.join('\n');
}

export function AdminShippingSettings() {
  const [rates, setRates] = useState([]);
  const [methodId, setMethodId] = useState('');
  const [district, setDistrict] = useState('');
  const [rate, setRate] = useState('');
  const [message, setMessage] = useState('');
  const [pickupSettings, setPickupSettings] = useState({
    enabled: false, address: '', city: '', phone: '', hours: ''
  });
  const [pickupMessage, setPickupMessage] = useState('');
  const [busyId, setBusyId] = useState('');

  const load = async () => {
    const rows = await getShipping();
    setRates(rows.flatMap((method) => (method.rates || [])
      .filter((item) => item.active !== false)
      .map((item) => ({...item, method_name: method.name, method_id: method.id}))));
    if (rows.length && !methodId) setMethodId(rows[0].id);
    try {
      setPickupSettings(await getStorePickupSettings());
    } catch {
      // Keep editable defaults if pickup settings have not been configured.
    }
  };

  useEffect(() => {
    load().catch(() => setMessage('Delivery settings could not be loaded.'));
  }, []);

  const addDistrict = async (event) => {
    event.preventDefault();
    try {
      await saveShippingDistrict({
        shipping_method_id: methodId, district, rate: Number(rate), active: true
      });
      setDistrict('');
      setRate('');
      setMessage('Delivery district and amount saved.');
      await load();
    } catch (error) {
      setMessage(error.message);
    }
  };

  const removeDistrict = async (item) => {
    if (!window.confirm(`Remove delivery to ${item.region}? Existing orders will be preserved.`)) return;
    setBusyId(item.id);
    try {
      await removeDeliveryDistrict(item.id);
      setMessage(`Delivery to ${item.region} removed.`);
      await load();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusyId('');
    }
  };

  const savePickup = async () => {
    setPickupMessage('');
    try {
      await saveStorePickupSettings(pickupSettings);
      setPickupMessage('Store pickup settings saved successfully.');
    } catch (error) {
      setPickupMessage(error.message);
    }
  };

  return (
    <div className="admin-overview-container">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Shipping & Delivery</h1>
          <p className="admin-page-subtitle">Configure delivery districts and store pickup options.</p>
        </div>
      </div>
      {message && <div className="admin-alert info">{message}</div>}
      <div className="admin-card-container" style={{marginBottom: 24}}>
        <div className="admin-card-header">
          <h3><SafeIcon icon={FiMapPin} style={{marginRight: 8}} /> Store Pickup</h3>
          <label className="admin-switch">
            <input type="checkbox" checked={pickupSettings.enabled}
              onChange={(event) => setPickupSettings((current) => ({
                ...current, enabled: event.target.checked
              }))} />
            <span>{pickupSettings.enabled ? 'Enabled' : 'Disabled'}</span>
          </label>
        </div>
        {pickupMessage && (
          <div className="admin-alert success" style={{marginTop: 0}}>
            <SafeIcon icon={FiCheckCircle} /> {pickupMessage}
          </div>
        )}
        <div className="form-grid-2">
          {[
            ['address', 'Store Address', 'e.g. 123 Main Street'],
            ['city', 'City', 'e.g. Colombo'],
            ['phone', 'Contact Phone', 'e.g. 077 123 4567'],
            ['hours', 'Pickup Hours', 'e.g. Mon-Sat 9:00 AM - 6:00 PM']
          ].map(([key, label, placeholder]) => (
            <label className="admin-field-label" key={key}>
              {label}
              <input type={key === 'phone' ? 'tel' : 'text'} value={pickupSettings[key]}
                placeholder={placeholder} onChange={(event) => setPickupSettings((current) => ({
                  ...current, [key]: event.target.value
                }))} />
            </label>
          ))}
        </div>
        <button className="admin-btn-primary" onClick={savePickup} style={{marginTop: 18}}>
          <SafeIcon icon={FiSave} /> Save Pickup Settings
        </button>
      </div>
      <div className="admin-card-container">
        <div className="admin-card-header"><h3>Delivery Districts & Charges</h3></div>
        <form onSubmit={addDistrict}>
          <div className="form-grid-2">
            <label className="admin-field-label">
              District
              <input required value={district} onChange={(event) => setDistrict(event.target.value)}
                placeholder="e.g. Colombo" />
            </label>
            <label className="admin-field-label">
              Delivery Amount (LKR)
              <input required type="number" min="0" value={rate}
                onChange={(event) => setRate(event.target.value)} placeholder="500" />
            </label>
          </div>
          <input type="hidden" value={methodId} />
          <button className="admin-btn-primary" type="submit" style={{marginTop: 18}}>
            <SafeIcon icon={FiPlus} /> Add District
          </button>
        </form>
        {rates.length ? (
          <div className="order-status-list" style={{marginTop: 24}}>
            {rates.map((item) => (
              <div className="order-status-row" key={item.id}>
                <span>{item.region}</span>
                <strong>{formatCurrency(item.rate)}</strong>
                <button type="button" className="table-action-btn delete"
                  disabled={busyId === item.id} onClick={() => removeDistrict(item)}
                  aria-label={`Remove ${item.region} delivery district`}>
                  <SafeIcon icon={FiTrash2} /> Remove
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="category-desc" style={{marginTop: 18}}>
            No delivery districts configured yet. Add districts above.
          </p>
        )}
      </div>
    </div>
  );
}

export function AdminPaymentSettings() {
  const [settings, setSettings] = useState([]);
  const [message, setMessage] = useState('');
  const [savingId, setSavingId] = useState('');
  const [bankFields, setBankFields] = useState({});
  const [bankExtra, setBankExtra] = useState('');

  const load = async () => {
    const data = await getPaymentSettings();
    setSettings(data);
    const bank = data.find((item) => item.method === 'bank_transfer');
    if (bank) {
      const parsed = parseBankInstructions(bank.customer_instructions);
      setBankFields(parsed.fields);
      setBankExtra(parsed.extra);
    }
  };

  useEffect(() => {
    load().catch(() => setMessage('Payment methods could not be loaded.'));
  }, []);

  const update = (id, field, value) => setSettings((current) =>
    current.map((item) => item.id === id ? {...item, [field]: value} : item)
  );

  const save = async (setting) => {
    setSavingId(setting.id);
    setMessage('');
    try {
      const payload = setting.method === 'bank_transfer'
        ? {...setting, customer_instructions: buildBankInstructions(bankFields, bankExtra)}
        : setting;
      await savePaymentSetting(payload);
      setMessage(`${paymentName(setting.method)} settings saved successfully.`);
      await load();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSavingId('');
    }
  };

  return (
    <div className="admin-overview-container">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Payment Methods</h1>
          <p className="admin-page-subtitle">Enable the payment choices shown during checkout.</p>
        </div>
      </div>
      {message && <div className="admin-alert info">{message}</div>}
      <div className="admin-category-grid">
        {settings.map((setting) => (
          <div className="admin-category-card" key={setting.id}>
            <div className="category-card-top">
              <h3>{paymentName(setting.method)}</h3>
              <label className="admin-switch">
                <input type="checkbox" checked={setting.enabled}
                  onChange={(event) => update(setting.id, 'enabled', event.target.checked)} />
                <span>{setting.enabled ? 'Enabled' : 'Disabled'}</span>
              </label>
            </div>
            {setting.method === 'bank_transfer' && (
              <div className="bank-fields-editor">
                <div className="admin-help-banner" style={{marginBottom: 14}}>
                  <SafeIcon icon={FiInfo} style={{marginRight: 6}} />
                  <strong>Bank Details</strong>
                  <p>Customers will see these details with copy buttons during checkout.</p>
                </div>
                {bankFieldTemplate.map((field) => (
                  <label className="admin-field-label" key={field.key} style={{marginBottom: 10}}>
                    {field.label}
                    <input type="text" value={bankFields[field.key] || ''}
                      placeholder={field.placeholder}
                      onChange={(event) => setBankFields((current) => ({
                        ...current, [field.key]: event.target.value
                      }))} />
                  </label>
                ))}
                <label className="admin-field-label" style={{marginTop: 14}}>
                  Payment instructions
                  <textarea rows="4" value={bankExtra}
                    placeholder="e.g. Include your order number as the transfer reference."
                    onChange={(event) => setBankExtra(event.target.value)} />
                </label>
                {buildBankInstructions(bankFields, bankExtra) && (
                  <div className="bank-preview-box">
                    <strong>Preview (shown to customers):</strong>
                    <pre>{buildBankInstructions(bankFields, bankExtra)}</pre>
                  </div>
                )}
              </div>
            )}
            {setting.method === 'qr_payment' && (
              <>
                <label className="admin-field-label">
                  QR code image URL
                  <input type="url" value={setting.qr_image_url || ''}
                    placeholder="https://example.com/payment-qr.png"
                    onChange={(event) => update(setting.id, 'qr_image_url', event.target.value)} />
                </label>
                <label className="admin-field-label" style={{marginTop: 14}}>
                  Customer instructions
                  <textarea rows="4" value={setting.customer_instructions || ''}
                    onChange={(event) => update(
                      setting.id, 'customer_instructions', event.target.value
                    )} />
                </label>
              </>
            )}
            {setting.method === 'cash_on_delivery' && (
              <p className="category-desc">
                No instructions are required. Enable this method and save it to make
                Cash on Delivery available at checkout.
              </p>
            )}
            <div className="category-card-footer">
              <button className="admin-btn-primary small" onClick={() => save(setting)}
                disabled={savingId === setting.id}>
                <SafeIcon icon={FiSave} />
                {savingId === setting.id ? 'Saving…' : 'Save Settings'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function paymentName(method) {
  if (method === 'cash_on_delivery') return 'Cash on Delivery (COD)';
  if (method === 'bank_transfer') return 'Bank Transfer';
  return 'QR Payment';
}